import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CreditsBalance from "../components/CreditBalance";
import Title from "../components/Title";
import SessionItem from "../components/SessionItem";
import { TertiaryButton } from "../components/Button";
import { ReactComponent as SupportIcon } from "../assets/icon_whatsapp.svg";
import HomeHorizontalTrainerList, {
  HomeTrainer,
} from "../components/HomeHorizontalTrainerList";
import trainerImg from "../assets/trainer.png";
import headerCreditsIcon from "../assets/header-credits-icon.svg";
import { getUserProfile, getUserSessions, getAllTrainers, getTrainerProfileWithAvailability, getAllGyms } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import BookingSuccessModal from "../components/BookingSuccessModal";
import PaymentResultModal from "../components/PaymentResultModal";
import { formatTimeSlot, utcTimeOfDayToLocal } from "../utils/timeUtils";
import { openSupportWhatsApp } from "../constants/support";

interface UserProfile {
  _id: string;
  name?: string;
  age?: number;
  sex?: string;
  experience?: string;
  hasGloves?: boolean;
  gloveSize?: number;
  hasHandwrap?: boolean;
  credits?: number;
  countryCode?: string;
  mobileNumber?: string;
  addresses?: any[];
  trainingPhotos?: string[];
  trainingVideos?: string[];
}

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { user, logout } = useAuth();

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [sessionsData, setSessionsData] = useState<{
    sessionsLast30Days: number,
    totalSessions: number,
    upcomingSessions: Array<{
      _id: string,
      date: string,
      time: string,
      trainer: string,
      sessionIn: string,
      status: string,
      trainerId?: string,
      trainerProfilePic?: string,
      trainerPhoneNumber?: string,
      trainerWhatsappNumber?: string,
    }>,
    completedSessions: Array<{
      _id: string,
      date: string,
      time: string,
      trainer: string,
      status: string,
      trainerId?: string,
      trainerProfilePic?: string,
    }>
  } | null>(null);
  const [trainers, setTrainers] = useState<HomeTrainer[]>([]);
  const [gyms, setGyms] = useState<HomeTrainer[]>([]);
  const [originalTrainers, setOriginalTrainers] = useState<any[]>([]);
  const [originalGyms, setOriginalGyms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<any>(null);
  const [showPaymentResult, setShowPaymentResult] = useState(false);
  const [paymentResultType, setPaymentResultType] = useState<"success" | "failure">("success");
  const [paymentCredits, setPaymentCredits] = useState<number | null>(null);

  // Load user profile and sessions data on component mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Load user profile, sessions data, and trainers in parallel
        console.log('Home - Starting to fetch data for user:', user.id);

        const [profileResponse, sessionsResponse, trainersResponse, gymsResp] = await Promise.all([
          getUserProfile(user.id),
          getUserSessions(user.id),
          getAllTrainers(),
          getAllGyms().catch(e => ({ gyms: [] }))
        ]);

        console.log('Home - Trainers response:', trainersResponse);
        console.log('Home - Trainers array:', trainersResponse.trainers);
        console.log('Home - Number of trainers:', trainersResponse.trainers?.length);

        setUserProfile(profileResponse.user);

        // Filter completed sessions from all sessions
        // Sessions are considered completed if they don't appear in upcomingSessions
        // or if their startDateTime is in the past
        const upcomingSessionIds = new Set(sessionsResponse.upcomingSessions?.map((s: any) => s._id) || []);
        const completedSessions = sessionsResponse.sessions?.filter((session: any) => {
          // Check if session is not in upcoming sessions
          const isNotUpcoming = !upcomingSessionIds.has(session._id);

          // Also check if the session date is in the past
          const sessionDate = new Date(session.startDateTime);
          const now = new Date();
          const isPastDate = sessionDate < now;

          return isNotUpcoming || isPastDate;
        }).map((session: any) => {
          // Format the session data to match the expected structure
          const sessionDate = new Date(session.startDateTime);
          const dateStr = sessionDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }).toUpperCase();

          const startTime = sessionDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });

          // Calculate end time (assuming 2-hour sessions)
          const endTime = new Date(sessionDate.getTime() + 2 * 60 * 60 * 1000);
          const endTimeStr = endTime.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });

          return {
            _id: session._id,
            date: dateStr,
            time: `${startTime} - ${endTimeStr}`,
            trainer: session.trainerName,
            status: 'completed',
            trainerId: session.trainerId || null,
            trainerProfilePic: session.trainerProfilePic || null,
          };
        }) || [];

        setSessionsData({
          sessionsLast30Days: sessionsResponse.sessionsLast30Days,
          totalSessions: sessionsResponse.totalSessions,
          upcomingSessions: sessionsResponse.upcomingSessions || [],
          completedSessions: completedSessions
        });

        // Transform trainer data to match HomeTrainer interface with real availability
        const formattedTrainers: HomeTrainer[] = (trainersResponse.trainers || [])
          .filter((trainer: any) => trainer._id) // Filter by trainer ID instead of name
          .slice(0, 6) // Limit to 6 trainers for home screen
          .map((trainer: any) => {
            try {
              // Generate next 3 days starting from tomorrow
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);

              const next3Days = [];
              for (let i = 0; i < 3; i++) {
                const date = new Date(tomorrow);
                date.setDate(tomorrow.getDate() + i);
                
                // Get the YYYY-MM-DD local format string
                const year = date.getFullYear();
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;

                const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                const dayName = dayNames[date.getDay()];

                // Look up slots for this specific date in the pre-calculated availabilitySlots map
                const daySlots = trainer.availabilitySlots?.[dateString];

                if (daySlots && Array.isArray(daySlots) && daySlots.length > 0) {
                  // Find the first available slot
                  const firstAvailableSlot = daySlots.find(slot => slot.available);

                  if (firstAvailableSlot) {
                    // slot.slot format is "2026-03-18T09:00:00.000Z-2026-03-18T10:00:00.000Z"
                    const parts = firstAvailableSlot.slot.split('-');
                    // find the split point (first '-' after the 'Z')
                    const zIndex = firstAvailableSlot.slot.indexOf('Z');
                    let startTimeLocal = "";
                    let endTimeLocal = "";
                    
                    if (zIndex !== -1) {
                        const startISO = firstAvailableSlot.slot.substring(0, zIndex + 1);
                        const endISO = firstAvailableSlot.slot.substring(zIndex + 2);
                        
                        // Parse UTC ISO strings back to local Date objects for formatting
                        const startDate = new Date(startISO);
                        const endDate = new Date(endISO);
                        
                        startTimeLocal = startDate.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        });
                        
                        endTimeLocal = endDate.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        });
                    }

                    next3Days.push({
                      day: i === 0 ? "Tomorrow" : dayName,
                      time: startTimeLocal && endTimeLocal ? `${startTimeLocal} - ${endTimeLocal}` : "Busy"
                    });
                  } else {
                    // Slots exist but none are available (e.g., all booked or all exceptions)
                    next3Days.push({
                      day: i === 0 ? "Tomorrow" : dayName,
                      time: "Busy"
                    });
                  }
                } else {
                  // No slots for this day at all
                  next3Days.push({
                    day: i === 0 ? "Tomorrow" : dayName,
                    time: "Busy"
                  });
                }
              }

              return {
                name: trainer.name || 'Trainer',
                rate: trainer.creditsPerHour || 0,
                age: trainer.age ? parseInt(trainer.age) : 0,
                sex: trainer.sex === 'Male' ? 'M' : trainer.sex === 'Female' ? 'F' : 'M',
                schedule: next3Days,
                trainingPhotos: trainer.trainingPhotos || [],
                imageUrl: trainer.signedProfilePic || trainer.profilePic || trainerImg as unknown as string,
              };
            } catch (error) {
              console.error(`Error processing availability for trainer ${trainer.name}:`, error);
              return {
                name: trainer.name || 'Trainer',
                rate: trainer.creditsPerHour || 0,
                age: trainer.age ? parseInt(trainer.age) : 0,
                sex: trainer.sex === 'Male' ? 'M' : trainer.sex === 'Female' ? 'F' : 'M',
                schedule: [], 
                trainingPhotos: trainer.trainingPhotos || [],
                imageUrl: trainerImg as unknown as string,
              };
            }
          });

        console.log('Formatted trainers for home:', formattedTrainers);

        setTrainers(formattedTrainers);
        setOriginalTrainers(trainersResponse.trainers || []);

        // Process Gyms
        const formattedGyms: HomeTrainer[] = (gymsResp?.gyms || []).map((gym: any) => {
           const next3Days = [];
           const tomorrow = new Date();
           tomorrow.setDate(tomorrow.getDate() + 1);
           
           for (let i = 0; i < 3; i++) {
             const date = new Date(tomorrow);
             date.setDate(tomorrow.getDate() + i);
             const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][date.getDay()];
             next3Days.push({
               day: i === 0 ? "Tomorrow" : dayName,
               time: "9:00 AM - 10:00 PM"
             });
           }

           return {
               name: gym.name,
               rate: gym.creditsPerHour || 5,
               age: 0,
               sex: 'M',
               schedule: next3Days,
               trainingPhotos: gym.photos || [],
               imageUrl: gym.profilePic || trainerImg as unknown as string,
               isGym: true
           };
        });
        setGyms(formattedGyms);
        setOriginalGyms(gymsResp?.gyms || []);
      } catch (error) {
        console.error("Error loading user data:", error);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user?.id]);

  // Refresh user profile when returning to this screen (e.g., from BuyCredits)
  useEffect(() => {
    const handleFocus = async () => {
      if (!user?.id || loading) return;

      try {
        console.log('Home - Refreshing user profile on focus');
        const profileResponse = await getUserProfile(user.id);
        setUserProfile(profileResponse.user);
        console.log('Home - Updated user profile:', profileResponse.user);
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    };

    // Listen for window focus events (when user returns to this tab/screen)
    window.addEventListener('focus', handleFocus);

    // Also refresh when the component becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, loading]);

  // Handle location state changes (e.g., returning from BuyCredits)
  useEffect(() => {
    const refreshUserProfile = async () => {
      if (!user?.id || loading) return;

      try {
        console.log('Home - Refreshing user profile due to location state change');
        const profileResponse = await getUserProfile(user.id);
        setUserProfile(profileResponse.user);
        console.log('Home - Updated user profile from location state:', profileResponse.user);
      } catch (error) {
        console.error('Error refreshing user profile from location state:', error);
      }
    };

    // Check if we have a refresh flag in location state
    if (location.state?.refreshCredits) {
      refreshUserProfile();
      // Clear the state to prevent unnecessary refreshes
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, user?.id, loading, navigate, location.pathname]);

  // Handle booking success from navigation state
  useEffect(() => {
    if (state?.bookingSuccess && state?.bookingDetails) {
      setBookingDetails(state.bookingDetails);
      setShowBookingSuccess(true);
      // Clear the state to prevent showing modal again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  // Handle payment result from navigation state
  useEffect(() => {
    if (state?.paymentResult) {
      setPaymentResultType(state.paymentResult === "failure" ? "failure" : "success");
      setPaymentCredits(typeof state.remainingCredits === "number" ? state.remainingCredits : null);
      setShowPaymentResult(true);
      // Clear the state to prevent showing modal again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  // Use real data from API
  const upcomingSessions = sessionsData?.upcomingSessions || [];
  const completedSessions = sessionsData?.completedSessions || [];

  // Show loading state while profile is loading
  if (loading) {
    return (
      <div className="h-[100dvh] w-full bg-[#F2F2F2] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading. Please wait.</p>
        </div>
      </div>
    );
  }

  // Show error state if profile loading failed
  if (error) {
    return (
      <div className="h-[100dvh] w-full bg-[#F2F2F2] flex items-center justify-center">
        <div className="text-center px-5 space-y-3">
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#D62422] text-white rounded"
            >
              Retry
            </button>
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded"
            >
              Back to Role Selection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // Make ONE full-viewport scroll container
    <div
      className="min-h-[100svh] w-full bg-[#F2F2F2] overflow-y-auto overflow-x-hidden pb-[env(safe-area-inset-bottom)]"
      style={{ paddingTop: "max(var(--safe-area-top), var(--app-vv-top, 0px))" }}
    >
      {/* Constrain to your 400px shell */}
      <div className="w-full min-h-[100dvh] flex flex-col">
        {/* Header */}
        <header
          className="relative w-full bg-[linear-gradient(157.07deg,#3A3A3A_0%,#252525_81.65%)] pt-8"
          style={{ paddingTop: "calc(2rem + max(var(--safe-area-top), var(--app-top-inset, 0px)))" }}
        >
          <img
            src={headerCreditsIcon}
            alt=""
            aria-hidden="true"
            className="absolute right-0 top-0 bottom-0 h-full object-contain z-0"
          />
          <div className="px-5 pt-5 relative z-10">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate("/student/profile")}
                className="flex items-center gap-2"
                aria-label="Profile"
              >
                <div className="h-9 w-9 grid place-items-center bg-[#252525] border border-[#727272]">
                  <svg width="20" height="20" viewBox="0 0 36 36" fill="none">
                    <path
                      d="M18 21C11 21 7.5 24 7.5 24V28.5H28.5V24C28.5 24 25 21 18 21Z"
                      stroke="#B0B0B0"
                      strokeWidth="2"
                    />
                    <path
                      d="M23 12.5C23 15.26 20.76 17.5 18 17.5C15.24 17.5 13 15.26 13 12.5C13 9.74 15.24 7.5 18 7.5C20.76 7.5 23 9.74 23 12.5Z"
                      stroke="#B0B0B0"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="text-white">
                  <div className="text-sm font-semibold leading-none">
                    {loading ? "Loading..." : userProfile?.name || "User"}
                  </div>
                </div>
              </button>
              <CreditsBalance
                balance={userProfile?.credits || 0}
                variant="dark"
                onAdd={() => navigate("/student/buy-credits")}
              />
            </div>

            <div className="mb-4 mt-6">
              <div className="flex items-baseline gap-2">
                <span className="text-[42px] leading-none italic font-extrabold text-[#EB2726]">
                  {loading ? "..." : sessionsData?.sessionsLast30Days || "0"}
                </span>
                <span className="text-white italic font-extrabold">SESSIONS</span>
              </div>
              <div className="text-[#B0B0B0] mt-1 text-sm">in last 30 days</div>
            </div>
          </div>
        </header>

        {/* Scrollable content */}
        <main className="flex-1 pb-20">
          {/* Upcoming - Only show if there are upcoming sessions */}
          {upcomingSessions.length > 0 && (
            <section className="px-5 pt-6">
              <Title text="// Upcoming Sessions" />
              <div
                className="mt-3 flex gap-4 overflow-x-auto snap-x snap-mandatory
                           [-ms-overflow-style:none] [scrollbar-width:none]
                           [&::-webkit-scrollbar]:hidden"
              >
                {upcomingSessions.map((s, i) => (
                  <div key={s._id} className="snap-start flex-shrink-0">
                    <SessionItem
                      date={s.date}
                      time={s.time}
                      trainer={s.trainer}
                      sessionIn={s.sessionIn}
                      isUpcoming
                      showFooter={i === 0}
                      onClick={() =>
                        navigate(`/student/session-details`, { state: { session: s } })
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <TertiaryButton
                  label="VIEW ALL"
                  layout="split"
                  onClick={() => navigate("/student/session-history")}
                  className="!w-full"
                />
              </div>
            </section>
          )}

          {/* Explore Trainers */}
          {trainers.length > 0 && (
            <div className="mb-6">
              <HomeHorizontalTrainerList
                trainers={trainers}
                gradient="dark"
                onCardClick={async (t, i) => {
                  const originalTrainer = originalTrainers[i];
                  const trainerId = originalTrainer?._id;
                  if (!trainerId) return;

                  let fullTrainerData = originalTrainer;
                  try {
                    const fullProfile = await getTrainerProfileWithAvailability(trainerId);
                    fullTrainerData = fullProfile;
                  } catch (error) {}

                  const trainerForDetails = {
                    _id: trainerId, 
                    name: fullTrainerData?.name || t.name,
                    totalSessions: fullTrainerData?.totalSessions || 0, 
                    creditsPerHour: fullTrainerData?.creditsPerHour || t.rate,
                    ageYears: fullTrainerData?.age ? parseInt(fullTrainerData.age) : t.age,
                    gender: fullTrainerData?.sex || (t.sex === 'M' ? 'Male' : 'Female'),
                    imageUrl: fullTrainerData?.signedProfilePic || fullTrainerData?.profilePic || t.imageUrl,
                    about: fullTrainerData?.aboutMe || 'Professional trainer with years of experience',
                    fullTrainerData: fullTrainerData,
                  };

                  navigate(`/student/trainer-details`, { state: { trainer: trainerForDetails } });
                }}
                onExploreAll={() => navigate("/explore-trainers")}
              />
            </div>
          )}

          {/* Explore Gyms */}
          {gyms.length > 0 && (
            <div className="mb-6">
              <HomeHorizontalTrainerList
                title="// Or train at our premium gyms"
                trainers={gyms}
                gradient="red" // Contrast from Trainers
                onCardClick={(t, i) => {
                  const gymTarget = originalGyms[i];
                  navigate('/student/select-pax', {
                    state: {
                      trainer: {
                        ...gymTarget,
                        isGym: true,
                        fullTrainerData: gymTarget
                      },
                      from: '/student/home'
                    }
                  });
                }}
                onExploreAll={() => navigate("/explore-trainers")} 
              />
            </div>
          )}

          {/* Completed - Only show if there are completed sessions */}
          {completedSessions.length > 0 && (
            <section className="px-5 pt-12">
              <Title text="// Completed Sessions" />
              <div
                className="mt-3 flex gap-4 overflow-x-auto snap-x snap-mandatory
                           [-ms-overflow-style:none] [scrollbar-width:none]
                           [&::-webkit-scrollbar]:hidden items-stretch"
              >
                {completedSessions.map((s, i) => (
                  <div key={s._id || i} className="snap-start flex-shrink-0">
                    <SessionItem
                      date={s.date}
                      time={s.time}
                      trainer={s.trainer}
                      sessionIn=""           // required by SessionItem props
                      isUpcoming={false}
                      showFooter={false}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <TertiaryButton
                  label="VIEW ALL"
                  layout="split"
                  onClick={() => {
                    console.log('Home - Navigating to student/session-history with activeTab: 1');
                    navigate("/student/session-history", { state: { activeTab: 1 } });
                  }}
                  className="!w-full"
                />
              </div>
            </section>
          )}

          {/* Footer */}
          <section className="px-5 pt-8 pb-2">
            <button
              onClick={openSupportWhatsApp}
              className="flex items-center gap-2 text-[#D62422] font-semibold"
              aria-label="Talk with us"
            >
              <SupportIcon className="w-5 h-5 flex-shrink-0" />
              <span>TALK WITH US</span>
            </button>
          </section>
        </main>
      </div>

      {/* Booking Success Modal */}
      <BookingSuccessModal
        isOpen={showBookingSuccess}
        onClose={() => setShowBookingSuccess(false)}
        bookingDetails={bookingDetails}
        onMessageTrainer={() => {
          setShowBookingSuccess(false);
          // TODO: Navigate to messaging
          console.log('Message trainer:', bookingDetails?.trainerName);
        }}
      />

      <PaymentResultModal
        open={showPaymentResult}
        type={paymentResultType}
        remainingCredits={paymentCredits ?? undefined}
        onClose={() => setShowPaymentResult(false)}
        onRetry={() => {
          setShowPaymentResult(false);
          navigate("/student/buy-credits");
        }}
        onBook={() => {
          setShowPaymentResult(false);
          navigate("/explore-trainers");
        }}
      />
    </div>
  );
}
